import { ref, computed, watch } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { workflowService } from '@/services/workflowService.js'
import { actionsService } from '@/services/actionsService.js'
import { useCurrentUser } from '../auth/useCurrentUser'
import { useAuth } from '../auth/useAuth'

/**
 * Invalidate nav badge queries - call this after workflow actions
 * @param {QueryClient} queryClient - The query client instance
 * @param {Object} options - Which badges to invalidate
 * @param {boolean} options.beoordelen - Invalidate beoordelen badge
 * @param {boolean} options.concretiseren - Invalidate concretiseren badge
 * @param {boolean} options.doen - Invalidate doen badge
 */
export function invalidateNavBadges(
  queryClient,
  { beoordelen = false, concretiseren = false, doen = false } = {}
) {
  if (beoordelen || concretiseren || doen) {
    queryClient.invalidateQueries({ queryKey: ['nav-badges'] })
  }
}

/**
 * Composable for navigation badge counts
 * Uses a single batch endpoint to fetch all badge data at once,
 * reducing HTTP requests from 4 to 1 on each refetch.
 */
export function useNavBadges() {
  const queryClient = useQueryClient()
  const { name: currentUserName } = useCurrentUser()
  const { userRole, userTeamIds, isAuthenticated } = useAuth()
  const matchedEmployeeId = ref(localStorage.getItem('nav_badge_employee_id'))

  // Team IDs come directly from auth (admin-managed user_team_assignments)
  const teamIds = computed(() => {
    if (userRole.value === 'performance_lead' && userTeamIds.value?.length > 0) {
      return userTeamIds.value
    }
    return null
  })

  // Build filter params for beoordelen badge queries
  const beoordelenParams = computed(() => {
    if (userRole.value === 'performance_lead' && teamIds.value?.length > 0) {
      return { responsible_team_ids: teamIds.value.join(',') }
    }
    return {}
  })

  // PLs must wait until teams are loaded; if PL has no teams, badge stays disabled (= 0)
  const beoordelenEnabled = computed(() => {
    if (userRole.value !== 'performance_lead') return true
    return teamIds.value !== null && teamIds.value.length > 0
  })

  // Employee ID matching: find current user's employee ID for "doen" badge
  const { data: employeesWithActions } = useQuery({
    queryKey: ['employees-with-actions'],
    queryFn: () => actionsService.getEmployeesWithActions(),
    staleTime: 1000 * 60 * 5,
    enabled: computed(
      () => isAuthenticated.value && !matchedEmployeeId.value && !!currentUserName.value
    )
  })

  watch(
    employeesWithActions,
    employees => {
      if (!employees || !currentUserName.value || matchedEmployeeId.value) return
      const matched = employees.find(
        e => e.name?.toLowerCase() === currentUserName.value?.toLowerCase()
      )
      if (matched) {
        matchedEmployeeId.value = matched.simplicate_id
        localStorage.setItem('nav_badge_employee_id', matched.simplicate_id)
      }
    },
    { immediate: true }
  )

  // Single batch query for all nav badge data
  const { data: badgeData } = useQuery({
    queryKey: ['nav-badges', beoordelenParams, matchedEmployeeId],
    queryFn: () =>
      workflowService.getNavBadges({
        ...beoordelenParams.value,
        employee_id: matchedEmployeeId.value || undefined
      }),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    enabled: computed(() => isAuthenticated.value && beoordelenEnabled.value)
  })

  const beoordelenCount = computed(() => {
    if (!badgeData.value) return 0
    return (badgeData.value.pending?.length || 0) + (badgeData.value.urgent?.length || 0)
  })

  const concretiserenCount = computed(() => badgeData.value?.unprocessed_count || 0)

  const doenCount = computed(() => {
    if (!badgeData.value?.my_actions) return 0
    return badgeData.value.my_actions.filter(a => a.status === 'open' || a.status === 'in_progress')
      .length
  })

  /**
   * Invalidate badge queries - use after workflow/action changes
   */
  const invalidateBadges = (options = { beoordelen: true, concretiseren: true, doen: true }) => {
    invalidateNavBadges(queryClient, options)
  }

  return { beoordelenCount, concretiserenCount, doenCount, invalidateBadges }
}
